import { createTheme, responsiveFontSizes, type Theme, alpha } from '@mui/material/styles'
import {
  lightColors,
  darkColors,
  fontFamily,
  fontSize,
  fontWeight,
  letterSpacing,
  lineHeight,
  spacingBase,
} from '@cmms/design-tokens'

function build(mode: 'light' | 'dark'): Theme {
  const c = mode === 'light' ? lightColors : darkColors
  const isDark = mode === 'dark'

  return createTheme({
    palette: {
      mode,
      primary:   { main: c.primary,   dark: c.primaryHover, contrastText: c.onPrimary },
      secondary: { main: c.secondary, contrastText: c.onSecondary },
      success:   { main: c.success,   contrastText: c.onSuccess },
      warning:   { main: c.warning,   contrastText: c.onWarning },
      error:     { main: c.danger,    contrastText: c.onDanger },
      info:      { main: c.info,      contrastText: c.onInfo },
      background: {
        default: c.background.default,
        paper:   c.background.paper,
      },
      text: {
        primary:   c.text.primary,
        secondary: c.text.secondary,
        disabled:  c.text.disabled,
      },
      divider: c.divider,
      action: {
        hover:    isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)',
        selected: isDark ? alpha(c.primary, 0.14)   : alpha(c.primary, 0.08),
        disabledBackground: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)',
      },
    },

    typography: {
      fontFamily: fontFamily.sans,
      fontWeightRegular: fontWeight.regular,
      fontWeightMedium:  fontWeight.medium,
      fontWeightBold:    fontWeight.semibold,

      h1: { fontFamily: fontFamily.display, fontSize: fontSize.h1, fontWeight: fontWeight.bold,     letterSpacing: letterSpacing.tighter, lineHeight: lineHeight.tight },
      h2: { fontFamily: fontFamily.display, fontSize: fontSize.h2, fontWeight: fontWeight.bold,     letterSpacing: letterSpacing.tight,   lineHeight: lineHeight.tight },
      h3: { fontFamily: fontFamily.display, fontSize: fontSize.h3, fontWeight: fontWeight.semibold, letterSpacing: letterSpacing.tight,   lineHeight: lineHeight.snug },
      h4: { fontSize: fontSize.h4, fontWeight: fontWeight.semibold, letterSpacing: letterSpacing.snug,   lineHeight: lineHeight.snug },
      h5: { fontSize: fontSize.h5, fontWeight: fontWeight.semibold, letterSpacing: letterSpacing.snug,   lineHeight: lineHeight.snug },
      h6: { fontSize: fontSize.h6, fontWeight: fontWeight.semibold, letterSpacing: letterSpacing.normal, lineHeight: lineHeight.snug },

      body1:    { fontSize: fontSize.bodyLarge, lineHeight: lineHeight.normal, letterSpacing: letterSpacing.normal },
      body2:    { fontSize: fontSize.body,      lineHeight: lineHeight.normal, letterSpacing: letterSpacing.normal },
      button:   { fontSize: fontSize.body,      fontWeight: fontWeight.semibold, letterSpacing: letterSpacing.normal, textTransform: 'none' },
      caption:  { fontSize: fontSize.caption,   lineHeight: lineHeight.snug,   letterSpacing: letterSpacing.wide },
      overline: { fontSize: fontSize.micro,     lineHeight: lineHeight.snug,   letterSpacing: '0.06em', textTransform: 'uppercase', fontWeight: fontWeight.semibold },
    },

    spacing: spacingBase,
    shape: { borderRadius: 8 },

    components: {
      MuiCssBaseline: {
        styleOverrides: {
          html: {
            // Scale base font with viewport: 14px at 1280, grows to ~16px at 1920
            fontSize: 'clamp(13.5px, 0.75vw + 10px, 16px)',
          },
          body: {
            WebkitFontSmoothing: 'antialiased',
            MozOsxFontSmoothing: 'grayscale',
          },
        },
      },

      MuiButton: {
        defaultProps: { disableElevation: true },
        styleOverrides: {
          root: {
            borderRadius: 8,
            textTransform: 'none',
            fontWeight: fontWeight.semibold,
            letterSpacing: letterSpacing.normal,
            transition: 'background-color 120ms ease, border-color 120ms ease, color 120ms ease',
          },
          sizeMedium: { padding: '7px 16px', minHeight: 36 },
          sizeLarge:  { padding: '10px 20px', minHeight: 44, fontSize: fontSize.bodyLarge },
          sizeSmall:  { padding: '5px 11px',  minHeight: 30 },
          outlined: {
            borderColor: c.border,
            '&:hover': { borderColor: c.text.tertiary, backgroundColor: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)' },
          },
        },
      },

      MuiIconButton: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            transition: 'background-color 120ms ease, color 120ms ease',
          },
        },
      },

      MuiCard: {
        defaultProps: { elevation: 0 },
        styleOverrides: {
          root: {
            borderRadius: 12,
            border: `1px solid ${c.divider}`,
            backgroundImage: 'none',
            backgroundColor: c.background.paper,
          },
        },
      },

      MuiPaper: {
        defaultProps: { elevation: 0 },
        styleOverrides: {
          root: { backgroundImage: 'none' },
        },
      },

      MuiTextField: {
        defaultProps: { size: 'medium', variant: 'outlined' },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            fontSize: fontSize.body,
            backgroundColor: isDark ? 'rgba(255,255,255,0.02)' : 'transparent',
            '& fieldset': { borderColor: c.border, transition: 'border-color 120ms ease' },
            '&:hover fieldset': { borderColor: c.text.tertiary },
            '&.Mui-focused fieldset': { borderWidth: 1.5 },
          },
          input: { padding: '11px 14px' },
          notchedOutline: { borderColor: c.border },
        },
      },
      MuiInputLabel: {
        styleOverrides: {
          root: { fontSize: fontSize.body, letterSpacing: letterSpacing.normal },
        },
      },
      MuiFormHelperText: {
        styleOverrides: {
          root: { fontSize: fontSize.caption, marginLeft: 2 },
        },
      },

      MuiChip: {
        styleOverrides: {
          root: {
            borderRadius: 6,
            fontWeight: fontWeight.semibold,
            fontSize: fontSize.caption,
            letterSpacing: letterSpacing.normal,
            height: 22,
          },
          outlined: { borderColor: c.border },
          filled: { fontWeight: fontWeight.semibold },
        },
      },

      MuiBackdrop: {
        styleOverrides: {
          root: {
            backdropFilter: 'blur(6px)',
            backgroundColor: isDark ? 'rgba(0, 0, 0, 0.45)' : 'rgba(15, 23, 42, 0.25)',
          },
        },
      },

      MuiDialog: {
        styleOverrides: {
          paper: {
            borderRadius: 14,
            border: `1px solid ${c.divider}`,
            boxShadow: isDark
              ? '0 24px 64px -16px rgba(0,0,0,0.8), 0 8px 16px -8px rgba(0,0,0,0.6)'
              : '0 24px 64px -16px rgba(15, 23, 42, 0.22), 0 8px 16px -8px rgba(15, 23, 42, 0.08)',
          },
        },
      },
      MuiDialogTitle: {
        styleOverrides: {
          root: { padding: '22px 26px 8px', fontSize: fontSize.h4, fontWeight: fontWeight.semibold, letterSpacing: letterSpacing.snug },
        },
      },
      MuiDialogContent: {
        styleOverrides: {
          // MUI's DialogContent forces `padding-top: 0` whenever it follows
          // a DialogTitle. That clips the top of floating labels (and
          // similar overlapping outline elements) on the first form
          // field, so we restore enough top room for the label to fit.
          root: {
            padding: '8px 26px 18px',
            '.MuiDialogTitle-root + &': { paddingTop: 12 },
          },
        },
      },
      MuiDialogActions: { styleOverrides: { root: { padding: '14px 22px 18px', gap: 8 } } },

      MuiMenu: {
        styleOverrides: {
          paper: {
            borderRadius: 10,
            border: `1px solid ${c.divider}`,
            backgroundColor: c.background.paper,
            boxShadow: isDark
              ? '0 10px 32px -8px rgba(0,0,0,0.7)'
              : '0 10px 32px -8px rgba(15, 23, 42, 0.18)',
            marginTop: 4,
          },
          list: { padding: 4 },
        },
      },
      MuiMenuItem: {
        styleOverrides: {
          root: {
            borderRadius: 6,
            margin: '1px 0',
            fontSize: fontSize.body,
            minHeight: 34,
            padding: '7px 10px',
            letterSpacing: letterSpacing.normal,
          },
        },
      },

      MuiTableCell: {
        styleOverrides: {
          root: {
            borderBottom: `1px solid ${c.divider}`,
            fontSize: fontSize.body,
            padding: '13px 18px',
            letterSpacing: letterSpacing.normal,
          },
          head: {
            fontWeight: fontWeight.semibold,
            color: c.text.secondary,
            fontSize: fontSize.micro,
            textTransform: 'uppercase',
            letterSpacing: '0.06em',
            padding: '10px 18px',
            backgroundColor: c.background.subtle,
            borderBottom: `1px solid ${c.divider}`,
          },
        },
      },
      MuiTableRow: {
        styleOverrides: {
          root: {
            '&:last-child td': { borderBottom: 'none' },
            '&:hover': { backgroundColor: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)' },
          },
        },
      },

      MuiTabs: {
        styleOverrides: {
          root: { minHeight: 42 },
          indicator: { height: 2, borderRadius: '2px 2px 0 0' },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: {
            minHeight: 42,
            padding: '10px 14px',
            fontSize: fontSize.body,
            fontWeight: fontWeight.semibold,
            textTransform: 'none',
            letterSpacing: letterSpacing.normal,
          },
        },
      },

      MuiAlert: {
        styleOverrides: {
          root: { borderRadius: 8, padding: '10px 14px', fontSize: fontSize.body, alignItems: 'center' },
          standardError:   { backgroundColor: c.dangerSoft,  color: c.danger },
          standardSuccess: { backgroundColor: c.successSoft, color: c.success },
          standardWarning: { backgroundColor: c.warningSoft, color: c.warning },
          standardInfo:    { backgroundColor: c.infoSoft,    color: c.info },
        },
      },

      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            fontSize: fontSize.caption,
            padding: '6px 10px',
            borderRadius: 6,
            fontWeight: fontWeight.medium,
            backgroundColor: isDark ? '#F5F5F7' : '#1D1D1F',
            color:            isDark ? '#1D1D1F' : '#FFFFFF',
          },
          arrow: {
            color: isDark ? '#F5F5F7' : '#1D1D1F',
          },
        },
      },

      MuiDivider: {
        styleOverrides: {
          root: { borderColor: c.divider },
        },
      },

      MuiAvatar: {
        styleOverrides: {
          root: { fontWeight: fontWeight.semibold, fontSize: fontSize.body },
          rounded: { borderRadius: 8 },
        },
      },

      MuiLinearProgress: {
        styleOverrides: {
          root: { borderRadius: 999, backgroundColor: c.divider },
          bar:  { borderRadius: 999 },
        },
      },
    },
  })
}

export const lightTheme = responsiveFontSizes(build('light'), { factor: 2.5 })
export const darkTheme  = responsiveFontSizes(build('dark'),  { factor: 2.5 })

/**
 * Create a theme variant with a custom primary colour.
 * We must create a fresh palette so MUI's `augmentColor` properly
 * derives `dark`, `light`, and `contrastText` from the new `main`.
 * A simple deep merge does NOT re-augment — it keeps the old derived values.
 */
export function withPrimaryColor(base: Theme, color: string): Theme {
  // Create a tiny theme just to get the augmented primary palette
  const temp = createTheme({ palette: { primary: { main: color } } })
  // Now merge the fully-derived primary into the base
  return createTheme({
    ...base,
    palette: {
      ...base.palette,
      primary: temp.palette.primary,
      action: {
        ...base.palette.action,
        selected: alpha(color, base.palette.mode === 'dark' ? 0.14 : 0.08),
      },
    },
  })
}
